import { createAction, createAsyncThunk } from "@reduxjs/toolkit";
import { APIError } from "../../../constants/types";
import { ERROR_MESSAGES } from "../../../constants/enums";
import { showMessage } from "../../messages/messagesActions";
import {
  buildIftaImportFormData,
  postIftaImportPreview,
} from "../../../helpers/iftaImportUpload";
import { IIftaImportPreview, ImportMode } from "./iftaImport.types";

/**
 * Dev-mode runtime sanity check on the preview response shape.
 *
 * The frontend treats the backend response as `res.data.data` and casts it to
 * `IIftaImportPreview` without any runtime validation. While the contract is
 * stable this cast is safe, but during rollout a silently-reshaped response
 * (e.g. `responser` nesting the payload differently, or the backend flattening
 * `territory.*`) would surface as confusing downstream `TypeError`s rather than
 * an obvious contract mismatch.
 *
 * This emits a single grouped `console.warn` when expected keys are missing. It
 * is a no-op in production builds so it never affects users (Gap task §5).
 */
export function assertPreviewShape(preview: IIftaImportPreview | undefined | null): void {
  if (process.env["NODE_ENV"] === "production") {
    return;
  }
  if (!preview || typeof preview !== "object") {
    console.warn(
      "[IFTA import] Preview response is empty or not an object. Expected res.data.data to contain the preview payload.",
    );
    return;
  }

  const missingTopLevel = (
    ["import_summary", "proposed", "current", "diff"] as const
  ).filter((key) => preview[key] === undefined || preview[key] === null);

  if (missingTopLevel.length > 0) {
    console.warn(
      `[IFTA import] Preview response is missing expected keys: ${missingTopLevel.join(
        ", ",
      )}. Check that the backend wraps the payload at res.data.data.`,
    );
  }

  // Only assert the nested quarterly breakdown shape once we actually have a
  // proposed section and the import is otherwise error-free.
  const errorCount = Array.isArray(preview.errors) ? preview.errors.length : 0;
  if (errorCount === 0 && preview.proposed) {
    const quarterly =
      preview.proposed.territory_covered_details?.["territory"]?.[
        "quarterly_breakdown"
      ];
    if (quarterly !== undefined && !Array.isArray(quarterly)) {
      console.warn(
        "[IFTA import] proposed.territory_covered_details.territory.quarterly_breakdown is expected to be an array but was not. The backend may have flattened or reshaped the territory nesting.",
      );
    }
  }
}

/**
 * Preview thunk for the IFTA quarterly import.
 *
 * Builds the multipart FormData via `buildIftaImportFormData` and POSTs it to the
 * preview endpoint via `postIftaImportPreview`. On success it returns the preview
 * payload (`res.data.data`). On failure it surfaces an error snackbar and rejects
 * with an `APIError` payload that carries the HTTP status so the reducer can detect
 * a 404 and hide the entry points (Requirement 3.6).
 */
export const previewIftaImportAsync = createAsyncThunk<
  IIftaImportPreview,
  {
    files: File[];
    customerPolicyId?: number;
    leadId?: string | null;
    incrementalNumber: number;
    mode: ImportMode;
    expectedQuarters?: number;
  },
  { rejectValue: APIError }
>("iftaImport/preview", async (args, { rejectWithValue, dispatch }) => {
  try {
    const fd = buildIftaImportFormData(args);
    const res = await postIftaImportPreview(fd);
    const preview = res.data.data as IIftaImportPreview;
    // Dev-mode safety net while the end-to-end contract stabilises (Gap §5).
    assertPreviewShape(preview);
    return preview;
  } catch (err: any) {
    const status = err.response?.status;
    const message = err.response?.data?.message || ERROR_MESSAGES.SERVER_ERROR;
    // A 404 means the preview endpoint is not deployed. This should degrade
    // silently by hiding the entry points (Req 3.6) rather than alarming the
    // user with an error snackbar. Only genuine failures (non-404) surface the
    // generic error snackbar (Req 3.5).
    if (status !== 404) {
      dispatch(
        showMessage({
          type: "error",
          message,
          displayAs: "snackbar",
        }),
      );
    }
    return rejectWithValue({
      message,
      status,
    });
  }
});

// Sync actions
export const setIftaMode = createAction<ImportMode>("iftaImport/setMode");
export const setIftaFiles = createAction<File[]>("iftaImport/setFiles");
export const ackIftaWarning = createAction<{ key: string; file_name?: string }>(
  "iftaImport/ackWarning",
);
export const applyIftaEdit = createAction<{ path: string; value: unknown }>(
  "iftaImport/applyEdit",
);
export const resetIftaImport = createAction("iftaImport/reset");
